import * as types from '../../actiontypes';

import {GetAllTasksUrl, GetAssignmentsByTasksIdUrl, GetTaskByIdUrl, createApiAction} from '../../apiUrls';
import {
	TaskCategory,
	TaskSubCategory,
	TaskTypeEnum,
	canvasUrlParams,
	promiseAreas,
	taskBuildMilestoneIds,
	taskSortingOrder
} from '../../../util/uiconstants';

/**
 * Returns an action to be dispatched and treated by the api-middleware, ultimately making an API call through Axios.
 * Represents a request for all a particular Task Group in the specified Engagement, from the specified Task Category and Sub-Category IDs.
 * @param {Number} engagementId The Engagement ID.
 * @param {Number} taskBuildMilestoneId The Task Milestone ID.
 * @param {String} orderBy The order by key to indicate the sorting order.
 * @param {Number} [categoryId = 2] The Task Category ID.
 * @param {Number} [subCategoryId = 1] The Task Sub-Category ID.
 * @param {boolean} includeCounts include counts
 * @param {boolean} includeBookmarks include bookmarks
 * @param {boolean} includePagination include pagination
 * @param {boolean} includeUIElements include UI Elements
 * @param {boolean} includeScheduledInfo include Scheduled Info
 * @param {boolean} includeAssignments include Assignments
 * @param {boolean} includeTaskList include task list
 * @returns {Object} An action object to be dispatched and treated by the api-middleware.
 */
export const getAllTasks = (
	engagementId,
	taskBuildMilestoneId = taskBuildMilestoneIds.GuidedWorkflow,
	orderBy,
	categoryId = TaskCategory.all,
	subCategoryId = TaskSubCategory.all,
	includeCounts = false,
	includeBookmarks = false,
	includePagination = false,
	includeUIElements = false,
	includeScheduledInfo = false,
	includeAssignments = false,
	includeTaskList = false,
	filterTaskIds = [],
	isAresV2 = false
) => {
	const parameters = {
		[canvasUrlParams.taskType]: TaskTypeEnum.PRIMARY_STANDARD_TASK,
		[canvasUrlParams.categoryId]: categoryId,
		[canvasUrlParams.subCategoryId]: subCategoryId,
		[canvasUrlParams.includeCounts]: includeCounts,
		[canvasUrlParams.includePagination]: includePagination,
		[canvasUrlParams.includeBookmarks]: includeBookmarks,
		[canvasUrlParams.includeUIElements]: includeUIElements,
		[canvasUrlParams.includeScheduledInfo]: includeScheduledInfo,
		[canvasUrlParams.includeAssignments]: includeAssignments,
		[canvasUrlParams.includeTaskList]: includeTaskList,
		[canvasUrlParams.taskBuildMilestone]: taskBuildMilestoneId,
		pageSize: 100
	};

	if (orderBy && orderBy !== '') {
		parameters['orderBy'] = orderBy;
	}

	const url = GetAllTasksUrl(engagementId, parameters, filterTaskIds);

	return createApiAction(types.GET_ALL_TASKS_BY_GROUP_ID, url, 'GET', undefined, undefined, {resolve: true, isAresV2});
};

export const getAllAresTasks = (engagementId, orderBy = taskSortingOrder.knowledge, filterTaskIds, isAresV2) => {
	return getAllTasks(
		engagementId,
		taskBuildMilestoneIds.GuidedWorkflow,
		orderBy,
		TaskCategory.all,
		TaskSubCategory.all,
		false,
		false,
		false,
		false,
		false,
		true,
		true,
		filterTaskIds,
		isAresV2
	);
};

/**
 * Returns an action to be dispatched and treated by the api-middleware, ultimately making an API call through Axios.
 * Represents a request for getting a task by its id.
 * @param {Number} engagementId The Engagement ID.
 * @param {Number} taskId The Task ID.
 * @param {string} queryString queryString if any
 * @returns {Object} An action object to be dispatched and treated by the api-middleware.
 */
export function getTaskById(engagementId, taskId, queryString) {
	const url = GetTaskByIdUrl(engagementId, taskId, queryString);

	const apiAction = createApiAction(
		types.ARES_GET_TASK_BY_ID,
		url,
		'GET',
		undefined,
		undefined,
		{resolve: true},
		undefined,
		promiseAreas.FIT_BREADCRUMB
	);
	apiAction.taskId = taskId;

	return apiAction;
}

export function updateTaskAssignmentStatus(engagementId, taskId, payload) {
	const url = GetTaskByIdUrl(engagementId, taskId);
	return {
		type: types.CHANGE_TASK_ASSIGNMENT_STATUS,
		meta: {
			api: {
				url: url,
				method: 'PATCH',
				data: payload
			}
		},
		payload
	};
}

export function updateTaskStatus(engagementId, taskId, payload) {
	const url = GetTaskByIdUrl(engagementId, taskId);
	return {
		type: types.CHANGE_TASK_STATUS,
		meta: {
			api: {
				url: url,
				method: 'PATCH',
				data: payload
			}
		},
		payload
	};
}

export function getAssignmentsByTaskId(engagementId, taskId) {
	const url = GetAssignmentsByTasksIdUrl(engagementId, taskId);
	const apiAction = createApiAction(types.GET_TASK_ASSIGNMENTS, url, 'GET');
	apiAction.taskId = taskId;
	return apiAction;
}
